import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Heart, 
  Leaf, 
  Sparkles, 
  CheckCircle2, 
  Users, 
  Phone, 
  Mail, 
  ArrowRight, 
  Shield, 
  Sun, 
  Compass,
  TreePine,
  Target,
  HelpCircle,
  Clock,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import useDocumentMeta from '../hooks/useDocumentMeta';

export default function LocalKolding() {
  const [openFaq, setOpenFaq] = useState(null);

  const faqData = {
    "@type": "FAQPage",
    "@context": "https://schema.org",
    "mainEntity": [
      {
        "name": "Hvad betyder beskyttet beskæftigelse efter servicelovens § 103?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Beskyttet beskæftigelse er et tilpasset beskæftigelsestilbud til voksne under folkepensionsalderen, som ikke kan arbejde på almindelige vilkår på grund af betydeligt nedsat fysisk eller psykisk funktionsevne eller særlige sociale udfordringer. Betegnelserne serviceloven 103, paragraf 103 og beskyttet beskæftigelse 103 henviser til den lovbestemmelse, som danner rammen om tilbuddet. Hos LIVSLYST tilpasses opgaver og støtte til den enkelte borgers ressourcer, interesser og dagsform.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvem kan få beskyttet beskæftigelse?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Målgruppen kan blandt andet være voksne med kognitive udfordringer, udviklingsforstyrrelser, autismespektrumforstyrrelser, erhvervet hjerneskade eller særlige sociale problemer. Hos LIVSLYST indledes der en dialog om borgerens støttebehov, interesser og trivsel for at vurdere, om det lille arbejdsfællesskab er det rette match.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvordan bliver man visiteret til beskyttet beskæftigelse?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Det er borgerens bopælskommune, der foretager en konkret vurdering og bevilger et tilbud efter servicelovens § 103. Borgere og pårørende kan derfor kontakte kommunen om visitation, mens sagsbehandlere og andre fagpersoner kan kontakte LIVSLYST for en dialog om målgruppe, støttebehov, match og aktuelle pladser.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvilke arbejdsopgaver kan indgå i et beskyttet beskæftigelsesforløb?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Hos LIVSLYST kan arbejdsdagen bestå af dyrepleje, havearbejde, vedligeholdelse af grønne områder, naturbaserede opgaver og kreative aktiviteter. Der er også fælles pauser, oplevelser og socialt samvær. Opgaverne har en tydelig funktion og bliver afstemt efter borgerens behov, interesser og dagsform.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvordan bliver arbejdsdagen tilpasset den enkelte borger?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "LIVSLYST arbejder med faste rutiner, tydelig kommunikation, visuel støtte og individuelt tilpassede krav. Den anerkendende tilgang og brugen af Low Arousal skal skabe ro og forebygge unødig stress. Udviklingen følges gennem individuelle mål, handleplaner, trivselssamtaler og løbende evaluering med fokus på personlige, sociale og praktiske færdigheder.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvordan kan arbejde med dyr og natur støtte udvikling og trivsel?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Arbejdet med dyr og natur kan skabe struktur, ansvar, omsorg og en oplevelse af at være nødvendig. Hos LIVSLYST bruges miljøet som en pædagogisk ramme, og opgaverne vælges sammen med borgeren. Det kan understøtte læring, arbejdsidentitet, deltagelse og større selvstændighed i et overskueligt tempo.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvor stort er fællesskabet i et beskyttet beskæftigelsestilbud?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "LIVSLYST har plads til seks borgere og tilbyder derfor et lille og overskueligt arbejdsfællesskab. Den lille ramme giver mulighed for genkendelighed, trygge relationer og tæt individuelt nærvær. Tilbuddet er relevant for borgere og fagpersoner, der søger beskyttet beskæftigelse nær Kolding i rolige og naturskønne omgivelser.",
          "@type": "Answer"
        }
      },
      {
        "name": "Kan man besøge et beskyttet beskæftigelsestilbud før opstart?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Hos LIVSLYST er det muligt at kontakte tilbuddet for at høre mere eller aftale en rundvisning. Et besøg kan give borgeren, de pårørende og kommunen et bedre grundlag for at vurdere, om de små rammer, arbejdsopgaverne, dyrene og naturen passer til den enkeltes behov.",
          "@type": "Answer"
        }
      }
    ]
  };

  useDocumentMeta(
    'Beskyttet beskæftigelse nær Kolding | Se mulighederne',
    'Oplev beskyttet beskæftigelse nær Kolding i trygge rammer med dyr, natur og individuelt tilpassede opgaver. Kontakt LIVSLYST i Brørup i dag.',
    ''
  );

  const targetGroupList = [
    'Personer med kognitive udfordringer eller udviklingsforstyrrelser',
    'Borgere med erhvervet hjerneskade og behov for tilpassede rammer',
    'Personer med betydeligt nedsat fysisk eller psykisk funktionsevne',
    'Borgere med særlige sociale udfordringer og en bevilling efter § 103'
  ];

  const workActivities = [
    {
      title: 'Dyrepleje',
      desc: 'Dyrepleje og genkendelige rutiner omkring dyrene.',
      icon: Heart
    },
    {
      title: 'Havearbejde',
      desc: 'Havearbejde og vedligeholdelse af grønne områder.',
      icon: TreePine
    },
    {
      title: 'Kreative opgaver',
      desc: 'Kreative opgaver med mulighed for fordybelse.',
      icon: Sparkles
    },
    {
      title: 'Fælles pauser',
      desc: 'Fælles pauser og aktiviteter der styrker relationer.',
      icon: Users
    }
  ];

  return (
    <div className="bg-stone-50 text-stone-800 antialiased overflow-x-hidden">
      
      {/* 1. HERO / HEADER SECTION */}
      <section className="bg-gradient-to-b from-stone-100 to-stone-50 py-16 md:py-24 border-b border-stone-200 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center space-x-2 text-xs md:text-sm text-stone-500 mb-2" aria-label="Brødkrummer">
            <Link to="/" className="hover:text-emerald-700 transition-colors">Forside</Link>
            <span>/</span>
            <span className="text-emerald-800 font-medium">Kolding</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 border border-emerald-300 text-emerald-900 text-xs font-semibold uppercase tracking-wider"
          >
            <MapPin className="w-3.5 h-3.5 text-emerald-700" />
            <span>Beskyttet beskæftigelse § 103 · For borgere nær Kolding</span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-tight"
          >
            Beskyttet beskæftigelse nær Kolding med plads til den enkelte
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed font-normal"
          >
            Når du leder efter beskyttet beskæftigelse nær Kolding, kan <Link to="/" className="font-semibold text-emerald-800 hover:text-emerald-900 transition-colors">LIVSLYST</Link> i Brørup være et relevant valg. Vi tilbyder en tryg arbejdsdag efter servicelovens § 103, hvor praktiske opgaver, tydelig struktur og et lille fællesskab giver plads til, at den enkelte kan deltage og udvikle sig i eget tempo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="pt-2 flex flex-wrap justify-center gap-4 text-sm"
          >
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-xl shadow-xs transition-colors"
            >
              <span>Kontakt os om pladser</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a 
              href="tel:51504654"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white hover:bg-stone-100 text-stone-800 font-medium rounded-xl border border-stone-300 transition-colors shadow-xs"
            >
              <Phone className="w-4 h-4 text-emerald-700" />
              <span>Ring på 51 50 46 54</span>
            </a>
          </motion.div>

          {/* Quick Trust Badges */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 max-w-3xl mx-auto border-t border-stone-200/80 text-left">
            <div className="flex items-center gap-2 text-xs sm:text-sm text-stone-700 bg-white/80 p-2.5 rounded-lg border border-stone-200">
              <Users className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>Maks. 6 borgere</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-stone-700 bg-white/80 p-2.5 rounded-lg border border-stone-200">
              <Leaf className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>Dyr & naturmiljø</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-stone-700 bg-white/80 p-2.5 rounded-lg border border-stone-200">
              <Shield className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>Servicelovens § 103</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-stone-700 bg-white/80 p-2.5 rounded-lg border border-stone-200">
              <Compass className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>Brørup (nær Kolding)</span>
            </div>
          </div>

        </div>
      </section>

      {/* 2. SECTION: ET OVERSKUELIGT BESKÆFTIGELSESTILBUD I BRØRUP */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            <motion.div 
              initial={{ opacity: 0, x: -25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 space-y-6"
            >
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
                <Compass className="w-4 h-4" />
                <span>Lokalitet & Nærvær</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
                Et overskueligt beskæftigelsestilbud i Brørup
              </h2>

              <p className="text-stone-600 leading-relaxed text-base sm:text-lg">
                Vores tilbud ligger på Surhavevej i Brørup, og vi tager gerne en dialog med borgere, pårørende og fagpersoner fra Kolding. Vi har ikke en afdeling i Kolding. Hos os foregår beskæftigelsen i naturskønne omgivelser, hvor nærvær, genkendelige rutiner og konkrete opgaver er en naturlig del af hverdagen.
              </p>

              <p className="text-stone-600 leading-relaxed text-base">
                Der er plads til seks borgere i beskæftigelsestilbuddet. Den lille ramme gør det muligt at kende den enkeltes interesser, behov for støtte og dagsform. Målet er en overskuelig hverdag med realistiske krav, medbestemmelse og mulighed for at opleve, at ens indsats har betydning.
              </p>

              <div className="p-5 rounded-2xl bg-stone-50 border border-stone-200 flex items-start gap-4">
                <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-800 shrink-0">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 text-sm sm:text-base">
                    Nærvær i det lille fællesskab
                  </h4>
                  <p className="text-stone-600 text-xs sm:text-sm mt-1 leading-relaxed">
                    Et overskueligt miljø med faste pædagogiske rammer og plads til den enkelte borgers tempo og trivsel.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right Media Card */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-lg border border-stone-200 bg-stone-100">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/img-7724.jpeg" 
                  alt="Fællesskab og trivsel i rolige rammer hos LIVSLYST" 
                  className="w-full h-80 sm:h-96 object-cover"
                  loading="lazy"
                />
                <div className="p-6 bg-stone-900 text-white">
                  <div className="flex items-center gap-2 text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-1">
                    <Heart className="w-4 h-4" />
                    <span>Trygge Relationer</span>
                  </div>
                  <p className="text-stone-300 text-sm leading-relaxed">
                    Maksimalt 6 pladser sikrer tæt støtte og ro omkring den enkelte borgers arbejdsdag.
                  </p>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* 3. SECTION: HVAD BETYDER SERVICELOVEN 103 FOR DIT FORLØB */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
                <Shield className="w-4 h-4" />
                <span>Lovgrundlag & Visitation</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
                Hvad betyder serviceloven 103 for dit forløb?
              </h2>
              <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
                Beskyttet beskæftigelse efter servicelovens § 103 er for personer under folkepensionsalderen, som på grund af betydeligt nedsat fysisk eller psykisk funktionsevne eller særlige sociale problemer ikke kan få eller fastholde arbejde på almindelige vilkår. Tilbuddet skal være bevilget af bopælskommunen.
              </p>
              <p className="text-stone-600 text-base leading-relaxed">
                Ordningen omtales også som serviceloven 103, <Link to="/paragraf-103" className="font-semibold text-inherit hover:text-emerald-800 transition-colors">paragraf 103</Link> og beskyttet beskæftigelse 103. Uanset navnet begynder et forløb med kommunal visitation og en vurdering af borgerens behov. Vi bidrager gerne med konkret information om hverdagen, målgruppen og mulighederne for et godt match.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="p-8 sm:p-10 rounded-3xl bg-white border border-stone-200 shadow-xs space-y-6">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
                  <Target className="w-4 h-4" />
                  <span>Kriterier & Målgruppe</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                  Hvem kan være i målgruppen for paragraf 103?
                </h3>
                <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
                  Målgruppen er sammensat, og en diagnose kan ikke stå alene. Det afgørende er den samlede funktionsevne, muligheden for beskæftigelse på normale vilkår og kommunens vurdering. Vores private beskæftigelsestilbud kan være relevant, når der er behov for tæt støtte og forudsigelige arbejdsrammer.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  {targetGroupList.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-stone-50 border border-stone-200/80">
                      <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-stone-800 leading-snug">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. SECTION: BESKYTTET BESKÆFTIGELSE 103 MED DYR OG NATUR */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
              <Leaf className="w-4 h-4" />
              <span>Natur & Meningsfulde Opgaver</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Beskyttet beskæftigelse 103 med dyr og natur
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Dyr og natur er en integreret del af vores læringsmiljø. Arbejdet kan blandt andet bestå af dyrepleje, faste opgaver omkring dyrene, havearbejde og vedligeholdelse af grønne områder. Opgaverne er praktiske og synlige, så borgeren kan mærke sammenhængen mellem indsats, ansvar og fællesskab.
            </p>
            <p className="text-stone-600 text-base leading-relaxed">
              Kontakt med dyr kan give anledning til at øve omsorg, struktur og ansvar. Naturen skaber samtidig rum for bevægelse, praktisk læring og rolige aktiviteter. Vi lover ikke bestemte resultater, men bruger miljøet målrettet til at understøtte trivsel, deltagelse og personlig udvikling.
            </p>
          </div>

          {/* Subsection: Sådan tilpasses arbejdsdagen til den enkelte */}
          <div className="p-8 sm:p-12 rounded-3xl bg-stone-50 border border-stone-200 mb-12">
            <div className="max-w-3xl mx-auto text-center space-y-4 mb-10">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-900">
                Sådan tilpasses arbejdsdagen til den enkelte
              </h3>
              <p className="text-stone-700 text-base sm:text-lg leading-relaxed">
                Arbejdsopgaverne vælges ud fra ressourcer, interesser, støttebehov og dagsform. Nogle har glæde af faste rutiner, mens andre har brug for variation eller tydelig visuel støtte. Vi arbejder anerkendende og med Low Arousal, så krav og kommunikation kan tilpasses uden at miste retning.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {workActivities.map((act, i) => {
                const IconComp = act.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="bg-white rounded-2xl p-6 border border-stone-200 flex flex-col justify-between hover:shadow-md transition-shadow"
                  >
                    <div className="space-y-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-700 text-white flex items-center justify-center shadow-xs">
                        <IconComp className="w-6 h-6" />
                      </div>
                      <h4 className="text-lg font-bold text-stone-900">{act.title}</h4>
                      <p className="text-stone-600 text-sm leading-relaxed">{act.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="max-w-3xl mx-auto bg-emerald-50/70 p-6 rounded-2xl border border-emerald-200/80 text-center">
              <p className="text-stone-800 leading-relaxed text-base">
                Udvikling følges gennem individuelle mål, handleplaner, trivselssamtaler og løbende evaluering. Fokus kan være større selvstændighed, kommunikation, ansvar eller deltagelse i fællesskabet. Målene skal være realistiske og give mening for borgeren i den konkrete hverdag.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* FAQ ACCORDION SECTION */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqData) }}
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 border border-emerald-300 text-emerald-800 text-xs md:text-sm font-semibold tracking-wide shadow-xs mb-4">
              <HelpCircle className="w-4 h-4 text-emerald-700" />
              <span>Spørgsmål & Svar</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-900 mb-4">
              Ofte stillede spørgsmål om beskyttet beskæftigelse
            </h2>
            <p className="text-stone-600 text-base sm:text-lg">
              Find svar på ofte stillede spørgsmål om vores tilbud, visitation, arbejdsopgaver og rammer for borgere nær Kolding.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 items-start">
            {/* Venstre kolonne */}
            <div className="space-y-3 sm:space-y-4">
              {faqData.mainEntity.slice(0, Math.ceil(faqData.mainEntity.length / 2)).map((item, index) => {
                const actualIndex = index;
                const isOpen = openFaq === actualIndex;
                return (
                  <div
                    key={actualIndex}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'bg-stone-50/80 border-emerald-300 shadow-xs'
                        : 'bg-white border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : actualIndex)}
                      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 cursor-pointer select-none focus:outline-hidden"
                      aria-expanded={isOpen}
                    >
                      <span className="text-base sm:text-lg font-semibold text-stone-900 leading-snug pr-2">
                        {item.name}
                      </span>
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isOpen ? 'bg-emerald-700 text-white rotate-180' : 'bg-stone-100 text-stone-600'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {isOpen && (
                      <div className="px-5 sm:px-6 pb-5 pt-1 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-200/60 mt-1">
                        <p>{item.acceptedAnswer.text}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Højre kolonne */}
            <div className="space-y-3 sm:space-y-4">
              {faqData.mainEntity.slice(Math.ceil(faqData.mainEntity.length / 2)).map((item, idx) => {
                const actualIndex = idx + Math.ceil(faqData.mainEntity.length / 2);
                const isOpen = openFaq === actualIndex;
                return (
                  <div
                    key={actualIndex}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'bg-stone-50/80 border-emerald-300 shadow-xs'
                        : 'bg-white border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : actualIndex)}
                      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 cursor-pointer select-none focus:outline-hidden"
                      aria-expanded={isOpen}
                    >
                      <span className="text-base sm:text-lg font-semibold text-stone-900 leading-snug pr-2">
                        {item.name}
                      </span>
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isOpen ? 'bg-emerald-700 text-white rotate-180' : 'bg-stone-100 text-stone-600'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {isOpen && (
                      <div className="px-5 sm:px-6 pb-5 pt-1 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-200/60 mt-1">
                        <p>{item.acceptedAnswer.text}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 5. SECTION: ER BESKYTTET BESKÆFTIGELSE NÆR KOLDING DET RETTE VALG? */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
              <HelpCircle className="w-4 h-4" />
              <span>Dialog & Afklaring</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Er beskyttet beskæftigelse nær Kolding det rette valg?
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
              Et godt match handler både om borgerens behov og om rammerne i tilbuddet. Derfor inviterer vi til en uforpligtende dialog og en rundvisning i Brørup, hvor borgeren, pårørende og relevante fagpersoner kan få indblik i arbejdsopgaverne, fællesskabet og den pædagogiske støtte.
            </p>
          </div>

          <div className="p-8 sm:p-10 rounded-3xl bg-white border border-stone-200 shadow-xs space-y-6">
            <p className="text-stone-700 leading-relaxed text-base sm:text-lg">
              Har du spørgsmål om visitation, målgruppe eller aktuelle pladser, er du velkommen til at kontakte os. Vi kan ikke bevilge et forløb, men vi kan hjælpe med at afklare, om LIVSLYST bør indgå i dialogen med bopælskommunen om beskyttet beskæftigelse nær Kolding.
            </p>

            <div className="pt-4 flex flex-wrap gap-4">
              <Link 
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-xl transition-colors shadow-xs"
              >
                <span>Kontakt os for en rundvisning</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a 
                href="tel:51504654"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-white hover:bg-stone-100 text-stone-800 font-medium rounded-xl border border-stone-300 transition-colors shadow-xs"
              >
                <Phone className="w-4 h-4 text-emerald-700" />
                <span>51 50 46 54</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* 6. OTHER LOCAL REGIONS QUICK LINKS */}
      <section className="py-12 bg-stone-100 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h3 className="text-lg font-bold text-stone-900">Beskyttet beskæftigelse i naboområder</h3>
              <p className="text-sm text-stone-600">Se vores tilbud målrettet andre nærliggende byer</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/beskyttet-beskaeftigelse-esbjerg"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-stone-50 border border-stone-300 text-stone-800 text-sm font-medium transition-colors"
              >
                <span>Beskyttet beskæftigelse nær Esbjerg</span>
                <ChevronRight className="w-4 h-4 text-emerald-700" />
              </Link>
              <Link
                to="/beskyttet-beskaeftigelse-vejen"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-stone-50 border border-stone-300 text-stone-800 text-sm font-medium transition-colors"
              >
                <span>Beskyttet beskæftigelse nær Vejen</span>
                <ChevronRight className="w-4 h-4 text-emerald-700" />
              </Link>
              <Link
                to="/paragraf-103"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium transition-colors"
              >
                <span>Læs alt om § 103</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 7. BOTTOM CTA CONTACT SECTION */}
      <section className="py-16 md:py-24 bg-stone-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-900/80 border border-emerald-700 text-emerald-300 text-xs font-semibold uppercase tracking-wider">
                <Phone className="w-3.5 h-3.5" />
                <span>Kontakt LIVSLYST ApS</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Ønsker du at høre mere om pladser eller booke en rundvisning?
              </h2>

              <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                Vi tager os god tid til en uforpligtende samtale og svarer på alle spørgsmål om målgruppe, pædagogik og visitation.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-xl transition-colors shadow-lg shadow-emerald-950/50"
                >
                  <span>Gå til kontaktsiden</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="tel:51504654"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-stone-800 hover:bg-stone-700 text-stone-200 border border-stone-700 font-medium rounded-xl transition-colors"
                >
                  <Phone className="w-4 h-4 text-emerald-400" />
                  <span>Ring 51 50 46 54</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 bg-stone-800/80 backdrop-blur-sm p-8 rounded-3xl border border-stone-700/80 space-y-6">
              <h3 className="text-lg font-bold text-white border-b border-stone-700 pb-4">
                Praktisk information
              </h3>
              
              <div className="space-y-4 text-sm text-stone-300">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-white block">Adresse:</span>
                    Surhavevej 14 A, 6650 Brørup
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-white block">Telefon:</span>
                    <a href="tel:51504654" className="hover:text-emerald-400 transition-colors">51 50 46 54</a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-white block">E-mail:</span>
                    <a href="mailto:kontakt@livslyst-aps.dk" className="hover:text-emerald-400 transition-colors">kontakt@livslyst-aps.dk</a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
